import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import StoreNavigation from '../components/StoreNavigation';
import StoreFooter from '../components/StoreFooter';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { collections, getCollectionBySlug } from '../data/mockProducts';
import { useProducts } from '../context/ProductsContext';
import { useCart } from '../context/CartContext';
import { Filter, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Shop = () => {
  const { category } = useParams();
  const { toast } = useToast();
  const { addToCart, getCartItemCount } = useCart();
  const { products, getProductsByCategory } = useProducts();
  const [selectedCategory, setSelectedCategory] = useState(category || 'all');
  const [showFilters, setShowFilters] = useState(false);

  const displayProducts = selectedCategory === 'all' 
    ? products 
    : getProductsByCategory(selectedCategory);

  const currentCollection = selectedCategory !== 'all' 
    ? getCollectionBySlug(selectedCategory) 
    : null;

  const handleAddToCart = (product) => {
    addToCart(product, product.sizes[0] || 'One Size', product.colors?.[0] || null, 1);
    toast({
      title: "Added to cart!",
      description: `${product.name} has been added to your cart.`,
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <StoreNavigation cartItemCount={getCartItemCount()} />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#004D51] to-[#003338] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-black mb-6">
              {currentCollection ? currentCollection.name : 'Shop All Products'}
            </h1>
            <p className="text-xl text-[#CFD2D4]">
              {currentCollection 
                ? currentCollection.description 
                : 'Professional refinishing gear designed for women in trades'}
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-6 py-12">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Sidebar - Filters & Collections */}
          <aside className="lg:w-64 flex-shrink-0">
            {/* Mobile Filter Toggle */}
            <Button
              onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden w-full mb-4 bg-[#004D51] hover:bg-[#003338] text-white font-bold"
            >
              <Filter className="w-4 h-4 mr-2" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>

            {/* Filters - Desktop always visible, Mobile toggleable */}
            <div className={`space-y-8 ${showFilters ? 'block' : 'hidden lg:block'}`}>
              {/* Collections */}
              <div>
                <h3 className="text-xl font-black text-[#004D51] mb-4 uppercase tracking-wide">
                  Collections
                </h3>
                <div className="space-y-2">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`w-full text-left px-4 py-3 rounded-lg font-bold transition-all ${
                      selectedCategory === 'all'
                        ? 'bg-[#DB5A14] text-white'
                        : 'bg-gray-100 text-[#545759] hover:bg-[#F3CEB2]'
                    }`}
                  >
                    All Products
                  </button>
                  {collections.map((collection) => (
                    <button
                      key={collection.id}
                      onClick={() => setSelectedCategory(collection.slug)}
                      disabled={collection.comingSoon}
                      className={`w-full text-left px-4 py-3 rounded-lg font-bold transition-all relative ${
                        selectedCategory === collection.slug
                          ? 'bg-[#DB5A14] text-white'
                          : collection.comingSoon
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-100 text-[#545759] hover:bg-[#F3CEB2]'
                      }`}
                    >
                      {collection.name}
                      {collection.comingSoon && (
                        <span className="ml-2 text-xs bg-[#545759] text-white px-2 py-1 rounded-full">
                          Soon
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Size Filter - Placeholder */}
              <div>
                <h3 className="text-xl font-black text-[#004D51] mb-4 uppercase tracking-wide">
                  Size
                </h3>
                <div className="grid grid-cols-3 gap-2">
                  {['XS', 'S', 'M', 'L', 'XL', 'XXL'].map((size) => (
                    <button
                      key={size}
                      className="px-3 py-2 bg-gray-100 hover:bg-[#F3CEB2] text-[#545759] font-bold rounded-lg transition-all text-sm"
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Filter - Placeholder */}
              <div>
                <h3 className="text-xl font-black text-[#004D51] mb-4 uppercase tracking-wide">
                  Price Range
                </h3>
                <div className="space-y-2">
                  <button className="w-full text-left px-4 py-2 bg-gray-100 hover:bg-[#F3CEB2] text-[#545759] font-medium rounded-lg transition-all">
                    Under $50
                  </button>
                  <button className="w-full text-left px-4 py-2 bg-gray-100 hover:bg-[#F3CEB2] text-[#545759] font-medium rounded-lg transition-all">
                    $50 - $100
                  </button>
                  <button className="w-full text-left px-4 py-2 bg-gray-100 hover:bg-[#F3CEB2] text-[#545759] font-medium rounded-lg transition-all">
                    Over $100
                  </button>
                </div>
              </div>

              {/* Help Section */}
              <div className="bg-[#F3CEB2]/30 rounded-xl p-6">
                <h3 className="text-lg font-black text-[#004D51] mb-2">
                  Need Help?
                </h3>
                <p className="text-sm text-[#545759] mb-4">
                  Not sure what size or which suit? We're here to help.
                </p>
                <Link to="/fit">
                  <Button className="w-full bg-[#004D51] hover:bg-[#003338] text-white font-bold">
                    View Fit Guide
                  </Button>
                </Link>
              </div>
            </div>
          </aside>

          {/* Product Grid */}
          <main className="flex-1">
            {/* Results Header */}
            <div className="flex items-center justify-between mb-8 pb-4 border-b-4 border-[#004D51]">
              <div>
                <h2 className="text-2xl font-black text-[#004D51]">
                  {displayProducts.length} {displayProducts.length === 1 ? 'Product' : 'Products'}
                </h2>
                <p className="text-sm text-[#545759] font-medium mt-1">
                  {currentCollection 
                    ? `Showing ${currentCollection.name}` 
                    : 'Showing all products'}
                </p>
              </div>

              {/* Sort - Placeholder */}
              <div className="hidden md:block">
                <select className="px-4 py-2 bg-white border-2 border-[#004D51] rounded-lg font-bold text-[#004D51] focus:outline-none focus:ring-2 focus:ring-[#DB5A14]">
                  <option>Featured</option>
                  <option>Price: Low to High</option>
                  <option>Price: High to Low</option>
                  <option>Newest</option>
                </select>
              </div>
            </div>

            {/* Product Grid */}
            {displayProducts.length > 0 ? (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">
                {displayProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onAddToCart={handleAddToCart}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="text-6xl mb-4">🔧</div>
                <h3 className="text-2xl font-black text-[#004D51] mb-2">
                  No Products Found
                </h3>
                <p className="text-[#545759] mb-6">
                  This collection is coming soon. Check back later!
                </p>
                <Link to="/shop">
                  <Button className="bg-[#DB5A14] hover:bg-[#F39C49] text-white font-bold">
                    View All Products
                  </Button>
                </Link>
              </div>
            )}

            {/* Info Banner */}
            <div className="mt-16 bg-[#004D51] rounded-2xl p-8 text-white text-center">
              <h3 className="text-2xl font-black mb-4">
                Questions About Our Products?
              </h3>
              <p className="text-[#CFD2D4] mb-6 max-w-2xl mx-auto">
                We're here to help you find the perfect fit. Check our fit guide, read our FAQ, or get in touch.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/fit">
                  <Button className="bg-[#DB5A14] hover:bg-[#F39C49] text-white font-bold px-8">
                    Fit Guide
                  </Button>
                </Link>
                <Link to="/support">
                  <Button className="bg-white/10 hover:bg-white/20 text-white font-bold px-8 border-2 border-white/30">
                    FAQ
                  </Button>
                </Link>
              </div>
            </div>
          </main>
        </div>
      </div>

      <StoreFooter />
    </div>
  );
};

export default Shop;
